'use strict';

import TWEEN from '../js/libs/tween.umd';
import {
    Buildings,
    cardsData, components, GSAPAnimationMixer, isDesktop, isDev,
    isMobile, isTablet, isWeb,
    projectMode, resourcesFolder,
    settingsData,
    threeJsVariables, UIRoot,
} from './settings';
import {add_tween_animation, animateCamera} from './modules/threejs/animation';
import {CardTypes, ViewMode} from "./common";
import {
    changeSelectionBoxesStatusColor,
    markSelectedApartments,
    searchGetResults, updateComponentsOnCanvasResize,
} from './modules/menu/search/search';

import {
    enableOrbitControls,
    enableTrackballControls,
    getCurrentCameraDistance,
    getOrbitControlsDistanceAndPan,
    getPolarAngleFromViewMode,
    getWorldData,
    resetPanLimits,
    setMinAndMaxControlsDistance,
    setPanLimits,
    setPolarAngle,
} from './modules/controls/orbitControls';
import {
    changeLangToCurrent,
    getCurrentLang,
    getCurrentLangDirection
} from "./modules/components/langSwitcher/langSwitcher";
import {
    setMenuActive
} from "./modules/menu/menu";
import {
    Box3,
    BufferGeometry,
    CatmullRomCurve3,
    BufferAttribute,
    Group,
    Mesh,
    EllipseCurve, Line, LineBasicMaterial,
    MeshBasicMaterial,
    MeshPhongMaterial,
    Texture,
    Vector2,
    Vector3,
} from 'three';
import {openOptionsCard} from "./modules/cards/optionsCards";
import {similarFavoriteHandle} from "./modules/menu/search/similarSearch";
import {analytics} from "./modules/analytics/analytics";
import {UnitCardClass} from "./modules/cards/unitCards/UnitCardClass";
import {setLordIcon} from './lordicon';
import {buildingHoverOut, intersectionClickHandler, selectionBoxesSetStatusColor} from './modules/threejs/raycast';
import {Spherical} from 'three';
import {OutlineEdges} from './libs/outlineEdges/OutlineEdges';
import {Favorite} from './modules/components/Favorite/Favorite';
import {BaseElement} from "./modules/interfaces/BaseElement";
import {getProps, getSales} from "./modules/crm/crm-requests";
import {IntersectionLegenda} from "./modules/components/IntersectionLegenda";
import gsap from 'gsap';
import Notiflix from "notiflix";
import {
    autoZoomIsActive,
    fitModelInViewport,
    getObjectViewDistance,
    getZoomParams,
} from './modules/controls/zoomControl';
import {
    cylinderFloorNumbers,
    removeCylinderFloorNumbers, rotateCylinderFloorNumbers,
} from './modules/components/cylinderFloorNumbers/cylinderFloorNumbers';
import {BmbyMode} from "./modules/mode/BmbyMode";
import { Lerp } from './modules/projectTime/Lerp';
import {renderUpdateValuesOnDemand} from "./app";
import { mergeGroups } from 'three/examples/jsm/utils/BufferGeometryUtils';

const md5 = require('md5');

export const RTL_LANGUAGES = ['he', 'ar'];

export function restrictUserInteractions(action) {
    action = !!action;

    enableTrackballControls(!action);
    enableOrbitControls(!action);
    userPrevent(action);
}

export function moveCameraToBuildingInCylinder(name) {
    const mixer = GSAPAnimationMixer.registerNewMixer('moveToBuilding');
    const building = Buildings.getBuilding(name);
    const buildingSelectionBoxes = building.selectionsGroup;
    const selectionBox = new Box3().setFromObject(buildingSelectionBoxes);
    const {minDistance, maxDistance} = getOrbitControlsDistanceAndPan(ViewMode.Cylinder);
    const {minAngle} = getPolarAngleFromViewMode(ViewMode.Cylinder);
    const buildingBoxCenter = selectionBox.getCenter(new Vector3());
    const buildingCenterPoint = buildingSelectionBoxes.getWorldPosition(new Vector3()).setY(buildingBoxCenter.y);
    const {cameraVector, orbitControlsVector} = getWorldData();

    if (document.getElementById('building-switcher')) {
        const activeItem = document.querySelector('#building-switcher .building-item.active');
        const targetItem = document.querySelector(`.building-item[data-building="${building.name}"]`);
        activeItem.classList.remove('active');
        targetItem.classList.add('active');
    }

    mixer.addCallback('onComplete', () => {
        cylinderFloorNumbers().then(() => rotateCylinderFloorNumbers());
        if (config.features.privateHouseMode) {
            setPolarAngle(ViewMode.Orbit);
            setMinAndMaxControlsDistance(ViewMode.Orbit);
            setPanLimits(ViewMode.Orbit);
        } else {
            setPolarAngle(ViewMode.Cylinder);
            setMinAndMaxControlsDistance(ViewMode.Cylinder);
            setPanLimits(ViewMode.Cylinder);
        }
        Buildings.deactivateBuilding();
        Buildings.activateBuilding(building.name);
        IntersectionLegenda.build();
        projectMode.Cylinder.setApartmentsToIntersection(building);
    });

    const cameraAnimation = animateCamera(
        cameraVector,
        cameraVector,
        orbitControlsVector,
        buildingCenterPoint,
        undefined,
        minDistance,
        minAngle,
        config.threejs.orbitControls.polarCameraCorrection,
        1000,
        TWEEN.Easing.Sinusoidal.InOut,
        () => {
            buildingHoverOut();
            resetPanLimits();
            removeCylinderFloorNumbers();
        }
    );

    mixer.addAnimation(cameraAnimation);
    mixer.play();
}

export async function flatClick(apartment) {
	if (settingsData.bombBlock && !settingsData.doubleClick || userPrevent())
		return;

	if (config.features.privateHouseMode) {
	    const activeBuilding = Buildings.getActiveBuilding();
	    const targetBuilding = apartment.userData.crm_data.buildingEngineId;
	    if (activeBuilding && activeBuilding.name !== targetBuilding) {
            moveCameraToBuildingInCylinder(targetBuilding);
        }
	}

    const id = apartment.userData.crm_data?.engineId;
    if (id) {
        projectMode.Cylinder.cylinderBuildingLabels.markLabel(id,'click');
        const openedCard = UnitCardClass.findCard(id);
        if (openedCard) {
            if (settingsData.doubleClick
                && (apartment === settingsData.selectedObject)
                && !document.querySelector(`.${CardTypes.Options}-card`))
                openOptionsCard();

            if (config.features.privateHouseMode) {
                markSelectedApartments(id);
            }
            return;
        }
    }

    cardsData.cardMode.isAmenities = apartment.userData?.customSelection;

    if (!config.features.privateHouseMode && apartment.userData.crm_data?.engineId !== '') {
        if ((!projectMode.LiteMode.modeIs('picture') && !projectMode.LiteMode.modeIs('empty')) && !cardsData.cardMode.isAmenities) {
            !settingsData.buildingDestroyed
                ? rotation_to_flat(apartment)
                : await moveAroundTheFloors(apartment.userData.crm_data.engineId);
        }
    }

    const floorIndex = apartment.userData?.floor;
    settingsData.currentFloor = !cardsData.cardMode.isAmenities ? floorIndex : settingsData.currentFloor;
    if (cardsData.cardMode.isAmenities) {
        if (isMobile) {
            components.mobileSearch.focusOnApartment(id);
        } else {
            openOptionsCard({object: apartment.userData?.crm_data?.engineId === '' ? apartment.userData.crm_data : undefined});
        }
    } else if (!apartment?.userData?.buildingFloors) {
        if (settingsData.doubleClick && !document.querySelector(`.${CardTypes.Options}-card`))
            openOptionsCard();

        if (isMobile) {
            components.mobileSearch.focusOnApartment(id);
        } else {
            UnitCardClass.cards.push(new UnitCardClass(apartment.userData.crm_data));
            const currentCard = UnitCardClass.cards.find(card => card.engineId === apartment.userData.crm_data.engineId);
            currentCard.createCard();
        }
    }

    if (isMobile) {
        markSelectedApartments(id);
    } else {
        if (!cardsData.cardMode.isAmenities)
            markSelectedApartments(id);
    }
}

export function lazyLoadingData(loader, url) {
    return new Promise((res, rej) => {
        loader.load(
            url,
            loadData => {
                res(loadData);
            },
            undefined,
            (err) => {
                settingsData.loadingError = true;
                rej(err);
            });
    });
}

export function lazyLoadingAll(promises) {
    return Promise.all(promises);
}

export function setColorsKeyPlan(currentFloorIndex = null) {
    const activeBuilding = Buildings.getActiveBuilding();

    for (const apt of activeBuilding.apartments) {
        const isFirstFloor = currentFloorIndex === 0 && apt.userData.floor === 0;

        if (apt.userData.isInSearch)
            continue;

        if (isFirstFloor || currentFloorIndex && apt.userData.floor === currentFloorIndex) {
            selectionBoxesSetStatusColor(apt, 'lock');
        } else {
            const searchIsOpen = document.querySelector('.search__container-main');
            if (!searchIsOpen || !apt.userData.isInSearch)
                selectionBoxesSetStatusColor(apt, 'unlock');
        }

        apt.userData.isClicked && selectionBoxesSetStatusColor(apt, 'click');
    }
}

export function isPortraitMode() {
    return window.matchMedia("(orientation: portrait)").matches;
}

// export function detectMobile() {
// 	let t = getUrlParam('type')
// 	if (t === 'android' || t === 'ios') {
// 		return true;
// 	} else {
// 		if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(navigator.userAgent)
// 			|| /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(navigator.userAgent.substr(0, 4))) {
//
// 			return true;
// 		}
// 	}
// 	return false;
// }

const isLastCharLetter = (input) => {
    const str = String(input);
    const lastChar = str.charAt(str.length - 1);
    return /^[a-zA-Z]$/.test(lastChar);
}

export function numberWithCommas(number, isPrice = false) {
    let param;
	switch (getCurrentLang()) {
		case 'en':
			param = 'en-US';
			break;
		case 'ar':
		case 'he':
			param = 'he-IL';
			break;
		// case 'ar':
		// 	param = 'ar-IL';
		// 	break;
		case 'el':
			param = 'el-GR';
			break;
		case 'fr':
			param = 'fr-FR';
			break;
		case 'es':
			param = 'es-ES';
			break;
		case 'ru':
			param = 'ru-RU';
			break;
		default:
			param = 'en-US';
	}


    if (isPrice) {
        if (!isLastCharLetter(number))
            number = number.toString().replace(/[,.\s]/g, '');
        const isShortPriceFormat = config.crmProjectSettings?.property?.settings?.priceFormat === 'short';
        const startFrom = config.features.apartments?.shortPrice?.startFrom || 1000000;
        if (isShortPriceFormat && startFrom <= number) {
            const fixedTo = config.features.apartments?.shortPrice?.fixedTo || 1;
            return number === '' ? number : formatNumber(parseFormattedNumber(number), startFrom, fixedTo);
        }

    } else {
        number = number.toString().replace(/[,\s]/g, '.');
    }

    if (parseInt(number) < 0 || (isPrice && isLastCharLetter(number)))
        return number;

    return parseFloat(number).toLocaleString(param);
}

// export function object_disappear(object, duration = 1000, delay = 0, easing = TWEEN.Easing.Linear.None) {
//   if (object.children.length > 0) {
//     object.children.forEach(function (children) {
//       object_disappear(children, duration, delay, easing);
//     });
//   } else {
//     let object_materials = object.material;
//     if (Array.isArray(object_materials)) {
//       object_materials.forEach(function (material) {
//         material_disappear(material, duration, delay, easing);
//       });
//     } else {
//       material_disappear(object_materials, duration, delay, easing);
//     }
//   }
//   setTimeout(function () {
//     object.visible = false;
//   }, duration);
// }

// export function object_appear(object, duration = 1000, delay = 0, easing = TWEEN.Easing.Linear.None) {
//   object.visible = true;
//   if (object.children.length > 0) {
//     object.children.forEach(function (children) {
//       object_appear(children, duration, delay, easing);
//     });
//
//   } else {
//     let object_materials = object.material;
//     if (Array.isArray(object_materials)) {
//       object_materials.forEach(function (material) {
//         material_appear(material, duration, delay, easing);
//       });
//     } else {
//       material_appear(object_materials, duration, delay, easing);
//     }
//   }
// }

// function material_disappear(material, duration, delay, easing) {
//   material.transparent = true;
//   if (!material.userData.base_opacity) {
//     material.userData.base_opacity = material.opacity;
//   }
//
//   add_tween_animation({
//     'animation_obj': settingsData.tween_animations,
//     'start': {
//       opacity: material.opacity
//     },
//     'target': {
//       opacity: 0
//     },
//     'duration': duration,
//     'easing': easing,
//     'delay': delay
//   }, function (e) {
//     material.opacity = e.opacity;
//   }, function (e) {
//     material.opacity = 0;
//   }, () => {
//   });
// }

// function material_appear(material, duration, delay, easing) {
//   material.transparent = true;
//   add_tween_animation({
//     'animation_obj': settingsData.tween_animations,
//     'start': {
//       opacity: material.opacity
//     },
//     'target': {
//       opacity: material.userData.base_opacity
//     },
//     'duration': duration,
//     'easing': easing,
//     'delay': delay
//   }, function (e) {
//     material.opacity = e.opacity;
//   }, function (e) {
//     material.opacity = material.userData.base_opacity;
//     // rendererAnimationPause(true);
//   }, () => {
//     // rendererAnimationPause(false)
//   });
// }

export function createHtmlElement(parent, elementName, elementTag, elementClass) {
    const el = document.createElement(elementTag);
    if (Array.isArray(elementClass)) {
        elementClass.forEach(classElement => {
            el.classList.add(classElement);
        });
    } else {
        el.style.cssText = elementClass;
    }
    el.innerHTML = elementName;
    if (parent) {
        parent.appendChild(el);
    }
    return el;
}

export function rotation_to_flat(apartment, smooth = true) {
    if (config.threejs.orbitControls.cylinder.controls === 'map') return;

    const {flatVector} = getWorldData(apartment);
    const delta = calculateDeltaFromVec3(flatVector);
    const from = threeJsVariables.orbitControls.getAzimuthalAngle();

    if (smooth) {
        const azimuth = {
            value: 0
        };

        if (from !== from + delta) {
            const animation = gsap.to(azimuth, {
                value: delta,
                duration: GSAPAnimationMixer.duration,
                ease: GSAPAnimationMixer.ease,
                onUpdate: () => threeJsVariables.orbitControls.setAzimuthAngle(from + azimuth.value),
                // onComplete: () => isMobile && zoomToTheFlat()
            });

            GSAPAnimationMixer.addToMixer('floorPlan', animation);
        }

    } else {
        threeJsVariables.orbitControls.setAzimuthDeltaAngle(delta);
    }

    return delta;
}

function zoomToTheFlat() {
    const dummy = {value: 0};
    gsap.to(
        dummy,
        {
            value: isMobile ? 0 : 0.3,
            duration: 0.5,
            ease: 'power2.out',
            onStart: () => {
                userPrevent(true)
            },
            onUpdate: () => {
                threeJsVariables.trackballControls.zoomCamera(dummy.value);
            },
            onComplete: () => {
                userPrevent(false)
            }
        }
    );
}

export function freezingCanvas (state) {
    if (state) {
       threeJsVariables.canvasResize = false;
    } else {
       threeJsVariables.canvasResize = true;
        updateComponentsOnCanvasResize();
    }
}

export function zoomToApartment (apt) {
    if (settingsData.activeViewMode !== ViewMode.Cylinder || projectMode.LiteMode.modeIs('picture'))
        return;

    const {flatVector,orbitControlsVector} = getWorldData(apt);
    const target = new Vector3(orbitControlsVector.x,flatVector.y,orbitControlsVector.z);
    
    const activeBuilding = Buildings.getActiveBuilding();
    const fitDist = getObjectViewDistance(activeBuilding.selectionsGroup);
    const optimalDist = getObjectViewDistance(activeBuilding.selectionsGroup,'width');
    
    const mixer = GSAPAnimationMixer.registerNewMixer('zoomToApt');
    mixer.addCallback('onStart',() => {
        threeJsVariables.orbitControls.enabled = false;
        threeJsVariables.trackballControls.enabled = false;
    });
    mixer.addCallback('onComplete',() => {
        threeJsVariables.orbitControls.enabled = true;
        threeJsVariables.trackballControls.enabled = true;
    });
    
    if (fitDist <= optimalDist) {
        target.y = orbitControlsVector.y;
    }
   
    const delta = calculateDeltaFromVec3(flatVector);
    const currentDelta = threeJsVariables.orbitControls.getAzimuthalAngle();
    const thetaLerp = new Lerp(currentDelta,currentDelta + delta);
    const distanceLerp = new Lerp(getCurrentCameraDistance(),optimalDist * 0.8);

    const orbitControlsAnimation = gsap.to(orbitControlsVector,{
        x: target.x,
        y: target.y,
        z: target.z,
        duration: GSAPAnimationMixer.duration,
        ease: GSAPAnimationMixer.ease,
        onUpdate: () => threeJsVariables.orbitControls.target.copy(orbitControlsVector)
    });
    
    mixer.addAnimation(orbitControlsAnimation);

    const cameraProgress = {
        value: 0
    };

    const direction = new Vector3();

    const cameraAnimation = gsap.to(cameraProgress,{
        value: 1,
        duration: GSAPAnimationMixer.duration,
        ease: GSAPAnimationMixer.ease,
        onUpdate: () => {
            threeJsVariables.orbitControls.setAzimuthAngle(thetaLerp.lerp(cameraProgress.value));
            direction.subVectors(threeJsVariables.perspectiveCamera.position,threeJsVariables.orbitControls.target);
            direction.normalize().setLength(distanceLerp.lerp(cameraProgress.value)).add(threeJsVariables.orbitControls.target);
            threeJsVariables.perspectiveCamera.position.copy(direction);
        }
    });
    mixer.addAnimation(cameraAnimation);
    mixer.play();
}


export async function zoomOut () {
    const activeBuilding = Buildings.getActiveBuilding();
    if (!activeBuilding.selectionsGroup || settingsData.activeViewMode !== ViewMode.Cylinder)
        return;

    const box = new Box3().setFromObject(activeBuilding.selectionsGroup);
    const center = new Vector3();
    box.getCenter(center);
    const distance = getObjectViewDistance(activeBuilding.selectionsGroup);
    const distanceLerp = new Lerp(getCurrentCameraDistance(),distance);
    const targetLerp = new Lerp(threeJsVariables.orbitControls.target.y,center.y);
    const distanceProgress = {
        value: 0
    };

    const direction = new Vector3();

    gsap.to(distanceProgress,{
        value: 1,
        duration: GSAPAnimationMixer.duration,
        ease: GSAPAnimationMixer.ease,
        onStart: () => {
            threeJsVariables.orbitControls.enabled = false;
            threeJsVariables.trackballControls.enabled = false;
        },
        onUpdate: () => {
            threeJsVariables.orbitControls.target.y = targetLerp.lerp(distanceProgress.value);
            direction.subVectors(threeJsVariables.perspectiveCamera.position,threeJsVariables.orbitControls.target);
            direction.normalize().setLength(distanceLerp.lerp(distanceProgress.value)).add(threeJsVariables.orbitControls.target);
            threeJsVariables.perspectiveCamera.position.copy(direction);
        },
        onComplete: () => {
            threeJsVariables.orbitControls.enabled = true;
            threeJsVariables.trackballControls.enabled = true;
        }
    })
}

export function enableSwitcherBtns(action) {
    const nav_btns = document.querySelectorAll('#building-switcher .new-ui-direction-btn');
    if (nav_btns.length > 0) {
        nav_btns.forEach(switcherBtn => {
            if (action) {
                switcherBtn.removeAttribute('disabled');
            } else {
                switcherBtn.setAttribute('disabled', 'true');
            }
        });
    }
}

// export function get_url_param(name) {
// 	const queryString = window.location.search;
// 	const urlParams = new URLSearchParams(queryString);
// 	if (urlParams) {
// 		let param = urlParams.get(name);
// 		if (param !== null) {
// 			return param;
// 		} else {
// 			return false;
// 		}
// 	}
// }

export function getUrlParam(name) {
    const url = new URL(window.location.href);
    const searchParam = url.searchParams.get(name);

    return searchParam;
}

export function isUrlHasParam(name) {
    const url = new URL(window.location.href);
    const isParamExist = url.searchParams.has(name);

    return isParamExist;
}

export function getUrlSearchQuery() {
    const url = new URL(window.location.href);

    return url.search;
}

export function checkHash(name) {
    const value = getUrlParam(name);
    config.general.hash = md5(window.location.host);
    return config.general.hash === value;
}

export function createMaterialAndSetBaseColor(apartment, addEdges = true) {

	const {active, color} = config.resources.bundle.selectionBoxes.edges;
	//
	// if (projectMode.LiteMode.isActive() && !projectMode.LiteMode.modeIs('regular')) {
	// 	apartment.material = new MeshPhongMaterial({
	// 		color: '#0d61af',
	// 		transparent: true,
	// 		opacity: 0,
	// 		depthWrite: false,
	// 		depthTest: true,
	// 	});
	//
	// 	if (active && addEdges) {
	// 		new OutlineEdges(apartment, {
	// 			color: `${color}`
	// 		});
	// 	}
	//
	// } else {

        apartment.material = new MeshBasicMaterial({
            color: '#0d61af',
            transparent: true,
            opacity: 0,
            depthWrite: false,
            depthTest: true,
        });

		const isAmenity = apartment.name.match(/_amenity_/g);
		// const allowedStatuses = ['Available', 'Saved', 'inSaving', 'Sold', 'Unavailable'];
		// const areCrmDataAndStatusPresent = apartment.userData.crm_data && allowedStatuses.includes(apartment.userData.crm_data.status);
		if (active && !isAmenity && apartment.userData.crm_data && addEdges) {
			new OutlineEdges(apartment, {
				color: `${color}`
			});
		}
	// }

    // apartment.material = new THREE.MeshBasicMaterial({
    //     color: '#0d61af',
    //     transparent: true,
    //     opacity: 0,
    //     depthWrite: false,
    //     depthTest: true,
    // });

}

export function removeSelectionBoxesLockColor(building, switchBuilding) {
    if (projectMode.LiteMode.isActive() && (projectMode.LiteMode.modeIs('picture') || projectMode.LiteMode.modeIs('empty')) || config.features.privateHouseMode) {
        const buildings = Buildings.getBuildingsArray();

        buildings.forEach(building => {
            const apartments = building.getApartments();
            apartments.forEach(apt => {
                apt.userData.isActive = false;
                apt.userData.isInSearch = false;
                apt.userData.isClicked = false;
                selectionBoxesSetStatusColor(apt, 'unlock');
            });

        });

        return;
    }

    if (!building) {
        building = Buildings.getActiveBuilding();
    }

    if (config.resources.bundle.selectionBoxes.edges.active && !projectMode.LiteMode.modeIs('schematic')) {
        const buildings = Buildings.getBuildingsArray();
        buildings.forEach(building => {
            building.apartments.forEach(apt => {
                const outline = apt.getObjectByName('Outline');
                if (outline) {
                    outline.visible = false;
                }
            });
        });
    }

    if (building?.apartments?.length > 0) {
        const apartmentWithAmenities = building?.apartments.concat(building.customSelections.filter(selection => selection.userData.crm_data.engineId !== ''));
        apartmentWithAmenities.forEach(item => {
            item.userData.isActive = false;
            item.userData.isInSearch = false;
            item.userData.isClicked = false;
            if (isMobile)
                selectionBoxesSetStatusColor(item, 'hoverOut', undefined, undefined, switchBuilding);
            selectionBoxesSetStatusColor(item, 'unlock', undefined, undefined, switchBuilding);
        });
    }
}

export function userPrevent(value = undefined) {
    if (value !== undefined) {
        settingsData.userPrevent = value;
    } else {
        return settingsData.userPrevent;
    }
}

export const changeBorderRadius = (element) => {
    // const root = document.querySelector(':root');
    // const borderShape = document.querySelector(':root').style.getPropertyValue('--buttonShape');
    const borderShape = getCSSVarValue('--buttonShape');
    if (borderShape === 'var(--roundedCorners)' || borderShape === 'var(--roundCorners)')
        setCSSVar('--specificRoundedCorners', '30px');
    // root.style.setProperty('--specificRoundedCorners', '30px');
    if (element.classList.contains('nmf-range-selector') && (borderShape === 'var(--roundedCorners)' || borderShape === 'var(--roundCorners)'))
        setCSSVar('--specificBorder', '3px')
    // root.style.setProperty('--specificBorder', '3px');
    if (element.classList.contains(`${CardTypes.Options}-card__plans-box_disclaimer-opacity`) &&
        (borderShape === 'var(--roundedCorners)' || borderShape === 'var(--roundCorners)'))
        setCSSVar('--specificBorderForDisclaimer', '4px');
    // root.style.setProperty('--specificBorderForDisclaimer', '4px');
}

// export const addHeader = (parent, title) => {
// 	if (isMobile && settingsData.isLandscape)
// 		return;
//
// 	parent.childNodes.forEach(elem => {
// 		if (elem === document.querySelector('.widget__container-title')) return;
// 	})
//
// 	new BaseElement('h2')
// 		.addClass('widget__container-title', 'language-string')
// 		.addAttribute('data-dictionary', title)
// 		.innerHtml(changeLangToCurrent(title))
// 		.appendTo(parent);
// }

// export const isElementScrolledTo = (element, direction) => {
//     switch (direction) {
//         case 'top':
//             return element.scrollTop === 0;
//         case 'bottom':
//             return Math.round(element.scrollHeight - element.scrollTop) - Math.round(parseFloat(getComputedStyle(element).height)) === 0;
//         case 'left':
//             return element.scrollLeft === 0;
//         case 'right':
//             return Math.round(element.scrollWidth - element.scrollLeft) - Math.round(parseFloat(getComputedStyle(element).width)) <= 0;
//         default:
//             console.log('isElementScrolledTo default case');
//     }
// }

// export const addCloseBtn = (parent) => {
//   const isCloseBtnExist = parent.querySelector('.widget__container-close');
//   if (isCloseBtnExist)
//     return;
//
//     new BaseElement('button')
//       .addClass('widget__container-close', 'icon-right')
//       .appendTo(parent)
//       .event('click', closeButtonHandler);
// }

// const closeButtonHandler = (parent) => {
//     parent.classList.remove('show-page');
//     if (isMobile) {
//         projectMode.Pagination.paginate({source: 'widget-close-button'});
//         playDownAllMenuButtons();
//         highlightMenuButtonDependsOnMode();
//     }
//
//     if (parent.classList.contains("search__container-main")) {
//         removeSearchContainer();
//         return;
//     }
//     settingsData.stopRender = false;
//     mobileBurgerStyleToggle();
//     mapElementStyleToggle(parent);
// }

export const highlightMenuButtonDependsOnMode = () => {
    const mapElement = document.body.querySelector('.widget__container[data-menu-item="map"]');
    if (mapElement && mapElement.classList.contains('show-page')) {
        setMenuActive('map');
        return;
    }
    switch (settingsData.activeViewMode) {
        case ViewMode.Orbit:
            setMenuActive('3dModel');
            break;
        case ViewMode.Cylinder:
            setMenuActive('search');
            break;
    }
}

export const lockScreenOrientation = () => {
    if (isWeb || isDesktop)
        return;

    const tableLockContainer = document.querySelector('.tablet-plug');
    const tabletUnlockRule = isTablet && settingsData.isLandscape;
    const tabletLockRule = isTablet && !settingsData.isLandscape;

    // const restrictedMobileOrientation = config.features?.mobileRestrictionOrientation?.active ? config.features?.mobileRestrictionOrientation?.type : false;
    // const isAllowedOrientation = restrictedMobileOrientation !== (settingsData.isLandscape ? 'landscape' : 'portrait');

    // const isOrbit = settingsData.activeViewMode === ViewMode.Orbit;
    // const isPopUpPage = document.querySelector('.pop-up-page:not(.hidden)');
    // const isContact = document.querySelector('.md-id.--show-dropdown');
    // const isVideoPopUp = document.querySelector('.video-pop-up[state="expanded"]');
    // const isMobileMenu = document.querySelector('.aside__container.mobile-menu__show');
    // const isScreenEmpty = !isPopUpPage && !isContact && !isMapOpened() && !isVideoPopUp && !isMobileMenu;

    const mobileUnlockRule = isMobile && !settingsData.isLandscape;
    const mobileLockRule = isMobile && settingsData.isLandscape;

    if (tableLockContainer && (tabletUnlockRule || mobileUnlockRule)) {
        tableLockContainer.remove();
    } else if (tabletLockRule || mobileLockRule) {
        lockScreenForTablet();
    }
}

const lockScreenForTablet = () => {
    if (document.querySelector('.tablet-plug'))
        return;

    const landscape = {
        en: "landscape",
        he: "נוֹף"
    };

    const portrait = {
        en: "portrait",
        he: "דְיוֹקָן"
    };

    const targetOrientation = settingsData.isLandscape ? portrait : landscape;
    // const orientationTranslations = {
    //   landscape: {
    //     en: "landscape",
    //     he: "נוֹף"
    //   },
    //   portrait: {
    //     en: "portrait",
    //     he: "דְיוֹקָן"
    //   }
    // };

    const chosenOrientation = {chosenOrientation: targetOrientation};

    // const chosenOrientation = orientationTranslations[targetOrientation];
    const titleText = `For maximum experience please rotate your device to {{orientation}}`;
    const subTitleText = 'if screen does not rotate, make sure your screen orientation is unlocked';

    const container = new BaseElement()
        .addClass('tablet-plug', 'non-canvas')
        .appendTo(document.body)
        .DOMElement;
    setLordIcon('rotate', container, {
        stroke: '100'
    });

    new BaseElement()
        .addClass('tablet-plug__title', 'language-string')
        .addAttribute('data-dictionary', 'tabletPlugTitle')
        .innerHtml(changeLangToCurrent('tabletPlugTitle', [chosenOrientation]))
        .appendTo(container);
    new BaseElement()
        .addClass('tablet-plug__subtitle', 'language-string')
        .addAttribute('data-dictionary', 'tabletPlugSubtitle')
        .innerHtml(changeLangToCurrent('tabletPlugSubtitle', [chosenOrientation]))
        .appendTo(container);
}

export function plugTexture() {
    const canvas = document.createElement('canvas');
    canvas.width = 4;
    canvas.height = 4;
    const context = canvas.getContext('2d');
    context.fillStyle = '#000000';
    context.fillRect(0, 0, 4, 4);
    return new Texture(canvas);
}

export const findSelectionAndCrmData = (engineId) => {
    const towerName = engineId.split('_')[0];
    const isAmenity = engineId.split('_')[1] === 'amenity';

    let activeTower;
    let selectedMesh;
    let crmDataOfFlat;
    activeTower = Buildings.getBuilding(towerName) || Buildings.getActiveBuilding();
    selectedMesh = activeTower[isAmenity ? 'customSelections' : 'apartments'].find(item => item.name === engineId);
    crmDataOfFlat = selectedMesh?.userData.crm_data;

    return {activeTower, selectedMesh, crmDataOfFlat};
}

export const handleFavoriteLogic = (e, action) => {
    if (e?.target?.classList.contains(`${CardTypes.Similar}-card__favorite-label`) || (e?.target?.tagName === 'LORD-ICON') && e?.target?.parentElement.classList.contains(`${CardTypes.Similar}-card__favorite-label`)) {
        similarFavoriteHandle(e);
        return;
    }

    let activeUnitCard = UnitCardClass.findCard(false, 'active')?.html;
    if (!cardsData.multiplyCards)
        activeUnitCard = UnitCardClass.findCard()?.html;

    let data;
    if (e.target.closest('.search__result-item')) {
        data = findSelectionAndCrmData(e.target.closest('.search__result-item').getAttribute('data-engineid'));
    } else if (e.target.closest(`.${CardTypes.Unit}-card`)) {
        data = findSelectionAndCrmData(e.target.closest(`.${CardTypes.Unit}-card`).getAttribute('data-engineid'));
    } else if (e.target.closest(`.${CardTypes.Options}-card`)) {
        data = findSelectionAndCrmData(e.target.closest(`.${CardTypes.Options}-card`).getAttribute('data-engineid'));
    } else if (e.target.closest(`.results-viewer__apartment-card`)) {
        data = findSelectionAndCrmData(e.target.closest(`.results-viewer__apartment-card`).getAttribute('data-engineid'));
    }

    if (BmbyMode.active) {
        const message = {
            action: 'MatchProperty',
            data: data.crmDataOfFlat.bmbyPropID
        };
        window.parent.postMessage(JSON.stringify(message), "*");
        console.log(message);
        return;
    }

    let storageData = getObjectFromLocalStorage(data.crmDataOfFlat.engineId);
    switch (action) {
        case ('searchClick'):
            e.target.classList.toggle('icon-favorite_selected');
            e.target.classList.toggle('icon-unselected');

            if (!storageData) {
                addObjectToLocalStorage(data.crmDataOfFlat);
                new Favorite(data.selectedMesh, 'add');
            } else {
                removeObjectFromLocalStorage(data.crmDataOfFlat);
                new Favorite(data.selectedMesh, 'remove');
            }

            if (activeUnitCard) {
                const currentCard = UnitCardClass.findCard(data.crmDataOfFlat.engineId)?.html;
                if (currentCard) {
                    const unitFavoriteLabel = currentCard.querySelector(`.${CardTypes.Unit}-card__favorite-label`);

                    unitFavoriteLabel.classList.toggle('icon-favorite_selected');
                    unitFavoriteLabel.classList.toggle('icon--lord-unselected');
                }
            }
            break
        case ('cardClick'):
            const toggleMobileApartmentCardFavoriteButton = (card) => {
                if (card) {
                    const labels = [...card.querySelectorAll(`.${CardTypes.MobileCard}-card__favorite-label`)];
                    labels.forEach(label => {
                        label.classList.toggle('icon-favorite_selected');
                        label.classList.toggle('icon--lord-unselected');
                        label.classList.toggle('icon-unselected');
                    });
                }
            }

            if (isMobile) {
                toggleMobileApartmentCardFavoriteButton(e.target.closest(`.results-viewer__apartment-card`));
            } else {
                const currentCard = UnitCardClass.findCard(data.crmDataOfFlat.engineId)?.html;
                if (currentCard) {
                    const label = currentCard.querySelector(`.${CardTypes.Unit}-card__favorite-label`);
                    label.classList.toggle('icon-favorite_selected');
                    if (label.classList.contains('icon--lord-unselected')) {
                        label.classList.toggle('icon--lord-unselected');
                    }
                    if (label.classList.contains('icon-unselected')) {
                        label.classList.toggle('icon-unselected');
                    }
                }

                const searchItemLabel = document.querySelector(`.search__result-item[data-engineid=${data.crmDataOfFlat.engineId}] span[data-favorite=favorite]`);
                if (searchItemLabel) {
                    searchItemLabel.classList.toggle('icon-favorite_selected');
                    searchItemLabel.classList.toggle('icon-unselected');
                }
            }

            if (!storageData) {
                addObjectToLocalStorage(data.crmDataOfFlat);
                new Favorite(data.selectedMesh, 'add');
            } else {
                removeObjectFromLocalStorage(data.crmDataOfFlat);
                new Favorite(data.selectedMesh, 'remove');
            }

            // if (isMobile)
            //     components.mobileSearch.update('favorite');

            const optionsCard = document.querySelector(`.${CardTypes.Options}-card`);
            if (optionsCard) {
                const currentOptionsCard = document.querySelector(`.${CardTypes.Options}-card[data-engineid=${data.crmDataOfFlat.engineId}]`);
                const optionsCardLabel = currentOptionsCard.querySelector(`.${CardTypes.Options}-card__favorite-label`);

                optionsCardLabel.classList.toggle('icon-favorite_selected');

                if (optionsCardLabel.classList.contains('icon--lord-unselected')) {
                    optionsCardLabel.classList.toggle('icon--lord-unselected');
                }
                if (isMobile) {
                    optionsCardLabel.classList.toggle('icon-unselected');
                    const engineId = optionsCard.getAttribute('data-engineid');
                    const mobileApartmentCard = document.querySelector(
                        `.results-viewer__apartment-card[data-engineid=${engineId}]`
                    );

                    toggleMobileApartmentCardFavoriteButton(mobileApartmentCard);
                }

            }

            break;
    }

    const crmDataOfFlat = data.crmDataOfFlat;
    analytics('favorites', {storageData, crmDataOfFlat});
}

export const addObjectToLocalStorage = (apartment) => {
    const favorites = localStorage.getItem('favorites');
    if (favorites?.length > 0) {
        localStorage.setItem('favorites', JSON.stringify([...JSON.parse(favorites), apartment]));
    } else {
        localStorage.setItem('favorites', JSON.stringify([apartment]));
    }
}

export const removeObjectFromLocalStorage = (apartment) => {
    let storageData = JSON.parse(localStorage.getItem('favorites'));
    if (!storageData)
        return;

    const favorites = storageData.filter(elem => elem.engineId !== apartment.engineId).flat();
    localStorage.setItem('favorites', JSON.stringify(favorites));
}

export function getObjectFromLocalStorage(engineId) {
    if (settingsData.lastClickedFlat?.userData?.customSelection)
        return;

    let storageData = JSON.parse(localStorage.getItem('favorites'));
    return storageData ? storageData.find(elem => elem.engineId === engineId) : null;
}

export function hasFavorites() {
    const storageData = JSON.parse(localStorage.getItem('favorites'));
    if (!storageData)
        return false;

    return storageData.some(apt => apt.status === 'Available');
}

export const moveAroundTheFloors = async (engineId) => {
    if (!settingsData.buildingDestroyed)
        return;
    const {selectedMesh} = findSelectionAndCrmData(engineId);
    const selectedFloor = selectedMesh.userData.floor;
    // let timeOut = 0;
    if (settingsData.currentFloor > selectedFloor) {
        await projectMode.FloorPlan.down(selectedFloor, selectedMesh);
    } else if (settingsData.currentFloor < selectedFloor) {
        await projectMode.FloorPlan.up(selectedFloor, selectedMesh);
    } else {
        rotation_to_flat(selectedMesh);
    }
}

export function rgbaToHexA(rgbaArray, addAlphaChanel = true) {
    let r = (+rgbaArray[0]).toString(16),
        g = (+rgbaArray[1]).toString(16),
        b = (+rgbaArray[2]).toString(16),
        a = Math.round(+rgbaArray[3] * 255).toString(16);

    if (r.length == 1)
        r = '0' + r;
    if (g.length == 1)
        g = '0' + g;
    if (b.length == 1)
        b = '0' + b;
    if (a.length == 1)
        a = '0' + a;

    if (addAlphaChanel) {
        return '#' + r + g + b + a;
    } else {
        return '#' + r + g + b;
    }
}

export function isCompareMode() {
    return Boolean(document.querySelector('.compare-block'));
}

export const makeStringNumbersStrong = (targetString) => {
    let resultString = '';
    const splitTargetString = targetString.split(' ');
    splitTargetString.forEach(element => {
        if (!isNaN(element)) {
            element = `<strong>${element}</strong>`
        }
        resultString += ' ' + element;
    });
    return resultString;
}

export const setParametersDependsOnCanvasArea = (element) => {
    const switcher = document.getElementById('building-switcher');

    if (!settingsData.isLandscape && element === 'building-switcher') {
        if (switcher) {
            switcher.style.removeProperty('left');
            switcher.style.removeProperty('transform');
        }

        return;
    }

    // const search = document.querySelector('.search__container-main');
    // if (!Boolean(search))
    //   return;

    setTimeout(() => {
        const additionalMenuContainer = document.querySelector('.additional-menu__container');
        const additionalMenuContainerWidth = additionalMenuContainer?.getBoundingClientRect().width;
        const unitCardWrapper = document.querySelector('.unit-card__wrapper');
        const unitCardWrapperWidth = unitCardWrapper?.getBoundingClientRect().width || 0;
        const searchContainerMain = document.querySelector('.search__container-main');
        const searchContainerMainWidth = searchContainerMain?.getBoundingClientRect().width || 0;
        const bodyWidth = document.body.getBoundingClientRect().width;
        const targetAreaWidth =
            bodyWidth -
            additionalMenuContainerWidth -
            unitCardWrapperWidth -
            searchContainerMainWidth;

        if (!Boolean(targetAreaWidth))
            return;

        switch (element) {
            case 'building-switcher':
                if (!switcher)
                    break;
                switcher.style.left = `${targetAreaWidth / 2}px`;
                switcher.style.transform = 'translateX(-50%)';
                break;
            case 'search-list':
                // const cssRoot = document.querySelector(':root')
                // cssRoot.style.setProperty(
                //   '--searchContainerBottomWidth',
                //   `${settingsData.isLandscape ? targetAreaWidth + 'px' : '100%'}`);
                setCSSVar(
                    '--searchContainerBottomWidth',
                    `${settingsData.isLandscape ? targetAreaWidth + 'px' : '100%'}`
                );
                break;
        }
    }, 100);
}

export const isOptionsCardOpened = () => {
    return Boolean(document.querySelector('.options-card'));
}

export const isStringHasChars = (customString) => {
    const regExp = /[a-z\u0590-\u05fe]/gi;
    return regExp.test(customString);
}

// export function getCookie(name) {
//   let matches = document.cookie.match(new RegExp(
//     "(?:^|; )" + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + "=([^;]*)"
//   ));
//   return matches ? decodeURIComponent(matches[1]) : undefined;
// }

// export function setCookie(name, value, options = {}) {
//   options = {
//     path: '/',
//     ...options
//   };
//
//   if (options.expires instanceof Date) {
//     options.expires = options.expires.toUTCString();
//   }
//
//   let updatedCookie = encodeURIComponent(name) + "=" + encodeURIComponent(value);
//
//   for (let optionKey in options) {
//     updatedCookie += "; " + optionKey;
//     let optionValue = options[optionKey];
//     if (optionValue !== true) {
//       updatedCookie += "=" + optionValue;
//     }
//   }
//
//   document.cookie = updatedCookie;
// }

// export const selectFirstAvailableApartmentOnTheFloor = (currentFloor) => {
// 	if (UnitCardClass.isAnyCardOpened())
// 		return;
//
// 	const currentFloorApartments = Buildings.getActiveBuilding().apartments.filter(mesh => {
// 		return parseInt(mesh.userData.floor) === parseInt(currentFloor);
// 	});
// 	const currentFloorAvailableApartments = currentFloorApartments.filter(apartment => {
// 		return apartment.userData.crm_data.status === 'Available';
// 	});
//
// 	let apartmentForSelect =
// 		currentFloorAvailableApartments.length === 0
// 			? currentFloorApartments[0]
// 			: currentFloorAvailableApartments[0]
//
// 	intersectionClickHandler(apartmentForSelect);
// };

export const isNumeric = (value) => {
    return ("boolean" !== typeof value) && (parseFloat(value) == value);
};

// export const debounce = (func, delay) => {
// 	let timer;
// 	return function (...args) {
// 		clearTimeout(timer);
// 		timer = setTimeout(() => {
// 			func(...args);
// 		}, delay);
// 	};
// };

export const updateProjectAPIData = async () => {
    await getProps().then((response) => {
        const newData = response.data;
        settingsData.allCrmData.forEach(data => {
            data.status = newData.find(el => el.engineId === data.engineId).status;
        });
        if (settingsData.activeViewMode !== ViewMode.Orbit) {
            searchGetResults();
            IntersectionLegenda.build();
        }
    });
};

export const kebabToCamelCase = (string) => {
    return string.replace(/-./g, x => x[1].toUpperCase());
};

export function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export function removeActiveGalleryItem() {
    const galleryOpenedItem = document.getElementsByClassName('fancybox__main-container show-fancybox-gallery')[0];
    if (!galleryOpenedItem) return;

    const fancyboxWrapper = galleryOpenedItem.getElementsByClassName('fancybox__wrapper')[0];

    galleryOpenedItem.classList.remove('show-fancybox-gallery');
    document.body.classList.remove('fancybox-active');
    fancyboxWrapper.innerHTML = '';
}

export function addWatermark() {
    const currentHostname = window.location.hostname;
    const hasBeta = currentHostname.includes("-beta");
    if (hasBeta || !config.crmProjectSettings.general?.production && !isDev) {
        new BaseElement()
            .addClass('watermark')
            .innerHtml('Under construction')
            .appendTo(document.body);
    }
}

export function areAllApartmentsSold(building) {
	if (BmbyMode.active) {
		const bmbyModeStatuses = [
			'Rent Contract',
			'Contract',
			'PreContract',
			'Registration Contract',
			'Occupied',
			'For Sale',
			'Not For Sale',
			'Reserved'
		];
		for (const apartment of building.apartments) {
			if (bmbyModeStatuses.includes(apartment.userData.crm_data.status))
				return false;
		}
	} else {
		for (const apartment of building.apartments) {
			if (apartment.userData.crm_data.status === 'Available')
				return false;
		}
	}

    return true;
}

export function anyApartmentsInSearch(building) {
    for (const apartment of building.apartments) {
        for (const result in settingsData.searchResult) {
            const currentItem = settingsData.searchResult[result];
            if (currentItem.engineId === apartment.name)
                return false;
        }
    }

    return true;
}

export function isRoofExists() {
    const currentActiveBuilding = Buildings.getActiveBuilding();

    return Boolean(currentActiveBuilding.amenities.Roof);
}

export function isFlatHas360Observation(mesh) {
    return mesh.userData.crm_data.facing.length === 4;
}

export function roundToNearest(value, step) {
    return Math.ceil(value / step) * step;
}

export function execGlobalThrottle(time = 500) {
    if (settingsData.globalThrottle)
        return;

    settingsData.globalThrottle = true;
    const timeout = setTimeout(() => {
        settingsData.globalThrottle = false;
        clearTimeout(timeout);
    }, time);
}

export function multimaterialNeedsUpdate() {
    this._targets[0].needsUpdate = true;
}

export function setSelectionBoxesColors() {
    settingsData.selectionBoxesDefaultColors.forEach(defaultColor => {
        let configColorExists = false;
        config.resources.bundle.selectionBoxes.colorStatus.forEach(configColor => {
            if (defaultColor.status === configColor.status)
                configColorExists = true;
        });

        if (!configColorExists)
            config.resources.bundle.selectionBoxes.colorStatus.push(defaultColor);
    });
}

export function getTowerName(building) {
    if (Array.isArray(building.info.towerName)) {
        return building.info.towerName.find(el => el.lang === getCurrentLang()).value;
    } else {
        return building.info.towerName;
    }
}

export function labelVisibility (data,state) {
	if (data.label) {
		data.label.visible = !state;
	}
	if (data.line) {
		data.line.visible = !state;
	}
}

export function calculateDeltaFromVec3(vec3,target = threeJsVariables.orbitControls.target) {
    // const {orbitControlsVector} = getWorldData();
    const directionVector = new Vector3().subVectors(vec3, target).normalize();
    const spherical = new Spherical().setFromVector3(directionVector);

    const from = threeJsVariables.orbitControls.getAzimuthalAngle();
    const to = spherical.theta;

    return getDelta(from,to);
}

export function getDelta (from,to) {
    const tempD = to - from;
    let delta;
    
    if (tempD < 0 && tempD < -Math.PI) {
        delta = tempD + 2 * Math.PI;
    } else if (tempD > 0 && tempD > Math.PI) {
        delta = tempD - 2 * Math.PI;
    } else {
        delta = tempD;
    }
    
    return delta;
}

export function clamp(num, min, max) {
    return Math.min(Math.max(num, min), max);
}

export function isImgLink(url) {
    if (typeof url !== 'string') return false;
    return (url.match(/^http[^\?]*.(jpg|jpeg|gif|png|tiff|bmp)(\?(.*))?$/gmi) != null);
}

export function setCSSVar(variableName, value) {
    document.querySelector(':root').style.setProperty(variableName, value);
}

export function getCSSVarValue(variableName) {
    return document.querySelector(':root').style.getPropertyValue(variableName);
}

export function isBetaResourcesFolder() {
    return resourcesFolder === 'resources_beta';
}

export function isOneMesh() {
    return config.resources.bundle.oneMesh;
}

export function getMoneyToken() {
    return new BaseElement('span')
        .addAttribute('data-dictionary', config.localization.currencyLabel)
        .innerHtml(changeLangToCurrent(config.localization.currencyLabel))
        .addClass('search__money-token', 'language-string');
}

export function getCurrentDateTime(format, timeStandard) {
    const now = new Date();

    // Determine if UTC is requested
    const isUTC = timeStandard === 'UTC';

    // Get the current date components based on time standard
    const year = isUTC ? now.getUTCFullYear() : now.getFullYear();
    const month = isUTC ? String(now.getUTCMonth() + 1).padStart(2, '0') : String(now.getMonth() + 1).padStart(2, '0');
    const day = isUTC ? String(now.getUTCDate()).padStart(2, '0') : String(now.getDate()).padStart(2, '0');

    // Get the current time components based on time standard
    const hours = isUTC ? String(now.getUTCHours()).padStart(2, '0') : String(now.getHours()).padStart(2, '0');
    const minutes = isUTC ? String(now.getUTCMinutes()).padStart(2, '0') : String(now.getMinutes()).padStart(2, '0');

    // Format the date and time based on the requested format
    switch (format) {
        case 'YYYY-MM-DD HH:MM':
            return `${year}-${month}-${day} ${hours}:${minutes}`;
        // You can add more case statements for different formats as needed
        case 'YYYY-MM-DD':
            return `${year}-${month}-${day}`;
        default:
            // Optionally, handle unsupported formats or provide a default format
            return `${year}-${month}-${day} ${hours}:${minutes}`;
    }
}

export function getDateAfarFromCurrent(daysAfar) {
    const date = new Date();

    date.setDate(date.getDate() + parseInt(daysAfar));

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

export function isCurrentDateWithinRange(startDateTime, endDateTime, currentDateTime) {
    if (!startDateTime || !endDateTime)
        return false;

    // Convert the date strings to Date objects
    const startDate = new Date(startDateTime);
    const endDate = new Date(endDateTime);
    const currentDate = new Date(currentDateTime);

    // Check if the current date is within the range
    return currentDate >= startDate && currentDate <= endDate;
}

export function isMapOpened() {
    const mapSelector = '.widget__container.show-page[data-menu-item="map"]';
    return Boolean(document.querySelector(mapSelector));
}

export function isCylinderDisplayModeEqual(mode) {
    return mode === config.features.cylinderDisplayMode?.mode;
}

export function getCylinderModeControlsType() {
    return config.features.cylinderDisplayMode?.controls === 'orbit' ? ViewMode.Orbit : ViewMode.Cylinder;
}

export function splitArray(arr, chunks) {
    return [...Array(chunks)].map((_, c) => arr.filter((n, i) => i % chunks === c));
}

export function getFileExtension(fileName) {
    return fileName.split('.')[1].toLowerCase();
}

export function showSoldApartmentsFromCurrentDay(daysAgo) {
    getSales(daysAgo).then((response) => {
        const contractsCount = settingsData.isConferenceHost ? 3 : response?.data?.contractsCount;

        if (contractsCount < 1 || !Boolean(contractsCount))
            return;

        const number = {number: {}};
        config.crmProjectSettings.languages.forEach(language => {
            number.number[language] = contractsCount;
        });

        const data = contractsCount === 1
            ? 'soldApartmentsNotificationOne'
            : 'soldApartmentsNotification';
        const container = new BaseElement()
            .addClass('notification');
        new BaseElement('span')
            .addClass('notification__text', 'language-string', 'sold-apartment-from__text')
            .addAttribute('data-dictionary', data)
            .innerHtml(changeLangToCurrent(data, contractsCount === 1 ? null : [number]))
            .appendTo(container.DOMElement);

		const color = 'fab446';
		Notiflix.Notify.merge({
			timeout: 5000,
			position: isDesktop ? 'left-top' : undefined,
			rtl: (getCurrentLangDirection() !== 'ltr'),
			info: {
				background: `#${color}`
			}
		});

		if (settingsData.activeViewMode !== ViewMode.Orbit)
			return;

		setTimeout(() => {
			Notiflix.Notify.info(`${container.DOMElement.innerHTML} `);
		}, 2000);
	})

}

// export function getLordIconColor() {
//   const {primary,secondary} = config.features.icons.color;
//
//   const primaryRgba = config.style.colors.find(color => color.key === primary) || { value: '255,255,255,1' };
//   const secondaryRgba = config.style.colors.find(color => color.key === secondary) || { value: '255,255,255,1' };
//   const activeRgba = config.style.colors.find(color => color.key === 'brand2');
//
//   const primaryColor = rgbaToHexA(primaryRgba.value.split(','),false);
//   const secondaryColor = rgbaToHexA(secondaryRgba.value.split(','),false);
//   const activeColor = rgbaToHexA(activeRgba.value.split(','), false);
//
//   return {primaryColor, secondaryColor, activeColor};
// }

export function isElementHovered(element) {
    return window.getComputedStyle(element, ':hover').pointerEvents !== 'none';
}

export function addAmenitiesWithoutEngineId(building) {
    if (!settingsData?.amenities)
        return;

    settingsData.amenities.forEach(amenity => {
        if (amenity.engineId === '' && amenity.buildingEngineID === building.name) {
            amenity.isAmenity = true;
            const amenityWithoutSelectionBox = {
                userData: {
                    customSelection: true,
                    crm_data: amenity
                }
            }

            building.customSelections.push(amenityWithoutSelectionBox);
        }

    })
}

export function truncateString(str, length) {
    if (str.length > length)
        return str.slice(0, length) + "...";

    return str;
}

export function isPageExist (dataSet) {
	return document.querySelector(`.widget__container[data-menu-item="${dataSet}"`);
}

export function captureError (err) {

	function getLineAndColumn(error) {
		const stackLines = error.stack.split('\n');
		const relevantLine = stackLines[1];
		const match = relevantLine.match(/:(\d+):(\d+)/);
		if (match) {
			const lineno = match[1];
			const colno = match[2];
			return { lineno, colno };
		} else {
			return {lineno: null, colno: null};
		}
	}

	const { lineno,colno } = getLineAndColumn(err);

	window.onerror(err.message,window.location.href,lineno,colno,err);
}

export async function checkImageExists(url) {
	try {
		const response = await fetch(url, { method: 'HEAD' });
		return response.ok;
	} catch (error) {
		return false;
	}
}

export function disposeObject (obj) {

	function disposeMaterial (material) {
		Object.values(material).forEach(value => {
			if (value.isTexture) {
				value.dispose();
			}
		});
		material.dispose();
	}

	if (obj.parent) {
		obj.removeFromParent();
	}

	let meshes = [];

	obj.traverse(el => {
		if (el.mesh) {
			meshes.push(el);
		}
	});

	meshes.forEach(mesh => {
		if (Array.isArray(mesh.material)) {
			mesh.material.forEach(material => disposeMaterial(material));
		} else {
			disposeMaterial(mesh.material);
		}
		mesh.geometry.dispose();
	});

	meshes = null;
}

export function filterDisabledLanguages(languages) {
	const disabledLanguages = config.features?.disabledLanguages || [];
	return languages.filter(language => !disabledLanguages.includes(language));
}

export function groupNumbersByDynamicRange(arrayOfNumbers) {
    if (arrayOfNumbers.length === 0)
        return [];

    arrayOfNumbers.sort((a, b) => a - b);

    // Calculate the range size
    const min = Math.floor(arrayOfNumbers[0]);
    const max = Math.ceil(arrayOfNumbers[arrayOfNumbers.length - 1]);
    const rangeSize = Math.ceil((max - min + 1) / 4); // Divide into 4 equal ranges

    // Create the ranges
    const ranges = [];
    for (let i = 0; i < 4; i++) {
        const start = formatNumber(min + i * rangeSize);
        const end = formatNumber(i === 3 ? max : min + (i + 1) * rangeSize - 1); // Last range includes the max
        ranges.push(i === 3 ? `${start}+` : `${start} - ${end}`);
    }

    // // Assign numbers to ranges
    // arrayOfNumbers.forEach(num => {
    //     for (let i = 0; i < ranges.length; i++) {
    //         const [start, end] = ranges[i].range.split("-").map(Number);
    //         if (num >= start && num <= end) {
    //             ranges[i].values.push(num);
    //             break;
    //         }
    //     }
    // });

    return ranges;
}

/**
 * Formats a number into a compact string using "K" for thousands and "M" for millions,
 * with an optional threshold to start formatting.
 *
 * @param {number} num - The number to format.
 * @param {number} [startFrom=1000] - The minimum value at which formatting should begin.
 *        Numbers below this threshold will be returned as-is (as a string).
 * @param {number} fixedTo - the number determined how much digits after decimal
 *
 * @returns {string} The formatted number:
 *   - If `num` is less than `startFrom`, it is returned as a string.
 *   - If `num` is between `startFrom` and 999,999, it is divided by 1,000 and suffixed with "K".
 *   - If `num` is 1,000,000 or more, it is divided by 1,000,000 and suffixed with "M".
 */
function formatNumber(num, startFrom = 1000, fixedTo = 0) {
    if (num < startFrom) return num.toString();
    if (num < 1_000_000) return (num / 1000).toFixed(fixedTo) + "K";
    return (num / 1_000_000).toFixed(fixedTo) + "M";
}

/**
 * Parses a formatted number string (e.g., "1.2k", "3.5M") and returns its numeric value.
 *
 * @param {string|number} formatted - The input value, which can be a number or a string
 *        representing a number with a suffix ("k" for thousands, "m" for millions).
 *        Examples: "1.2k", "500", 1000, "3M".
 *
 * @returns {number|null} The numeric value represented by the input.
 *          - If the input is a number, it is returned as-is.
 *          - If the input is a valid string with a recognized suffix ("k" or "m"),
 *            it is converted to the corresponding numeric value.
 *          - If the input is an unrecognized string or cannot be parsed, returns `null`.
 */
export function parseFormattedNumber(formatted) {
    if (typeof formatted === "number")
        return formatted;

    const multiplier = {
        k: 1000,
        m: 1000000
    };

    const unit = formatted.trim().slice(-1).toLowerCase();
    const value = parseFloat(formatted.trim().slice(0, -1));

    if (!isNaN(formatted))
        return Number(formatted);

    if (isNaN(value) || !multiplier[unit])
        return null;

    return value * multiplier[unit];
}


export function createMeshFromMultiMaterial (mesh,materialIndex) {
	const geometry = mesh.geometry;
	const index = geometry.index;
	const groups = geometry.groups;
	const attributeNames = Object.keys( geometry.attributes );
	const group = groups[ materialIndex ];

	const start = group.start;
	const end = start + group.count;

	const newGeometry = new BufferGeometry();
	const newMaterial = mesh.material[ group.materialIndex ];

	for ( let j = 0; j < attributeNames.length; j ++ ) {

		const name = attributeNames[ j ];
		const attribute = geometry.attributes[ name ];
		const itemSize = attribute.itemSize;

		const newLength = group.count * itemSize;
		const type = attribute.array.constructor;

		const newArray = new type( newLength );
		const newAttribute = new BufferAttribute( newArray, itemSize );

		for ( let k = start, n = 0; k < end; k ++, n ++ ) {

			const ind = index.getX( k );

			if ( itemSize >= 1 ) newAttribute.setX( n, attribute.getX( ind ) );
			if ( itemSize >= 2 ) newAttribute.setY( n, attribute.getY( ind ) );
			if ( itemSize >= 3 ) newAttribute.setZ( n, attribute.getZ( ind ) );
			if ( itemSize >= 4 ) newAttribute.setW( n, attribute.getW( ind ) );

		}

		newGeometry.setAttribute( name, newAttribute );
	}

	return new Mesh( newGeometry, newMaterial );
}

export function applyShortBuildingNameRule() {
    Buildings.getBuildingsArray().forEach(building => {
        if (building.info && building.getInfo().crm_data) {
            building.info.towerName.map(item => {
                const {lang, value} = item;

                if (value.length <= 3) {
                    item.value = `${c.dictionary['building'][lang]} ${value}`;
                }

                return item;
            });
        }
    });
}

export function isPerpendicularLines (vec1Start,vec1End,vec2Start,vec2End) {
	const direction1 = new Vector2().subVectors(vec1Start,vec1End);
	const direction2 = new Vector2().subVectors(vec2Start,vec2End);

	return Math.abs(direction1.dot(direction2)) < 0.0001;
}

export function isPerpendicularPoints (v1,v2) {
	const direction = new Vector2().subVectors(v2,v1);
	const h = new Vector2(1,0);
	const v = new Vector2(0,1);
	const dotH = Math.abs(direction.clone().dot(h));
	const dotV = Math.abs(direction.clone().dot(v));

	return dotV < 0.0001 || dotH < 0.0001;
}

export const getFieldsFromAPI = {
    smallCard: function() {
        const fieldsFromConfig = config.features?.apartments?.aptCardFieldsCntUnit;
        const fieldsFromAPI = config.crmProjectSettings?.fields?.smallCard;
        const parsedAPIFields = fieldsFromAPI && fieldsFromAPI
            .sort((a, b) => a.order - b.order)
            .map(item => translateAPIIntoDreamsName(item.key))

        return fieldsFromConfig || parsedAPIFields;
    },
    fullCard: function() {
        const fieldsFromConfig = config.features?.apartments?.aptCardFieldsCntOptions;
        const fieldsFromAPI = config.crmProjectSettings?.fields?.fullCard;
        const parsedAPIFields = fieldsFromAPI && fieldsFromAPI
            .sort((a, b) => a.order - b.order)
            .map(item => translateAPIIntoDreamsName(item.key))

        return fieldsFromConfig || parsedAPIFields;
    },
    filter: function () {
        let fieldsFromConfig;
        isMobile
            ? fieldsFromConfig = config.features.search?.searchConfig?.fieldsMobile || config.features.search?.searchConfig?.fields
            : fieldsFromConfig = config.features.search?.searchConfig?.fields || config.features.search?.searchConfig?.fieldsMobile;
        const fieldsFromAPI = config.crmProjectSettings?.fields?.filter;
        const parsedAPIFields = fieldsFromAPI && fieldsFromAPI.map(field => {
            return parseFilterField(field, 'filter');
        });

        return fieldsFromConfig || parsedAPIFields;
    },
    table: function () {
        const fieldsFromConfig = config.features.search?.searchSortStateMobile
            ? config.features.search.searchSortStateMobile
            : config.features.search?.searchSortState;
        const fieldsFromAPI = config.crmProjectSettings?.fields?.table;
        const parsedAPIFields = fieldsFromAPI && fieldsFromAPI.map(field => {
            return parseFilterField(field, 'table');
        });

        const favorite = {
            order: -2,
            key: 'favorite',
            type: 'favorite',
            icon: 'icon-unselected',
            state: 'minmax',
            isActive: false
        }
        const propNum = {
            order: -1,
            key: 'propNum',
            type: 'propNum',
            icon: '',
            text: '#',
            state: 'maxmin',
            isActive: true
        }
        parsedAPIFields.unshift(favorite, propNum);

        const tableFields = fieldsFromConfig || parsedAPIFields;
        if (isMobile)
            tableFields.length = 5;

        return tableFields;
    }
}

export function translateAPIIntoDreamsName(name) {
    switch(name) {
        case "floorNum":
            return "floor";
        case "modelName":
            return 'apt. type';
        case "roomNum":
            return 'bedrooms';
        case "totalSpace":
            return 'area';
        case "balconySize":
            return 'balcony size';
        case 'facing':
            return 'exposure';
        case 'roofBalconySize':
            return 'roof balcony size';
        case 'coveredBalconySize':
            return 'covered balcony size';
        case 'basementSize':
            return 'basement size';
        case 'bathRooms':
            return 'bathrooms';
        case 'gardenSize':
            return 'garden size';
        case 'storage':
            return 'storages';
        case 'parking':
            return 'parkings';
        default:
            return name;
    }
}

function parseFilterField(field, type) {
    switch (field.key) {
        case 'accessible':
            field.icon = 'icon-gift';
            if (type === 'filter') {
                field.labelKey = "Accessible";
            } else if (type === 'table') {
                field.type = field.key;
                field.state = 'minmax';
                field.isActive = false;
                field.visibilty = 'check-mark';
            }
            break;
        case 'balconySize':
            field.icon = 'icon-balcony';
            if (type === 'filter') {
                field.labelKey = field.key;
                field.visibilty = 'check-mark'
            } else if (type === 'table') {
                field.type = field.key;
                field.state = 'minmax';
                field.isActive = false;
            }
            break;
        case 'basementSize':
            field.icon = 'icon-floorplan1';
            if (type === 'filter') {
                field.labelKey = field.key;
                field.type = 'range';
            } else if (type === 'table') {
                field.type = field.key;
                field.state = 'minmax';
                field.isActive = false;
            }
            break;
        case 'bathRooms':
            field.icon = "icon-bathroom";
            if (type === 'filter') {
                field.labelKey = 'Bathrooms';
            } else if (type === 'table') {
                field.type = 'Bathrooms';
                field.state = 'minmax';
                field.isActive = false;
            }
            break;
        case 'roofBalconySize':
        case 'coveredBalconySize':
            if (field.key === 'coveredBalconySize') {
                field.labelKey = 'covered balcony size';
            } else {
                field.labelKey = field.key;
            }

            field.icon = 'icon-balcony';
            if (type === 'filter') {
                field.type = 'range';
            } else if (type === 'table') {
                field.type = field.key;
                field.state = 'minmax';
                field.isActive = false;
            }
            break;
        case 'facing':
            field.icon = 'icon-exposure';
            if (type === 'filter') {
                field.labelKey = field.key;
            } else if (type === 'table') {
                field.type = field.key;
                field.state = 'minmax';
                field.isActive = false;
            }
            break;
        case 'floorNum':
            field.icon = 'icon-floor';
            if (type === 'filter') {
                field.labelKey = 'floor';
            } else if (type === 'table') {
                field.type = field.key;
                // field.type = 'floor';
                field.state = 'minmax';
                field.isActive = false;
            }
            break;
        case 'gardenSize':
            field.icon = 'icon-tree-n-grass';
            if (type === 'filter') {
                field.labelKey = field.key;
                field.type = 'range';
            } else if (type === 'table') {
                field.type = field.key;
                field.state = 'minmax';
                field.isActive = false;
            }
            break;
        case 'managementFee':
        case 'maintenanceFee':
            if (type === 'filter') {
                field.labelKey = field.key;
                field.icon = 'icon-settings';
                field.type = 'range'
            } else if (type === 'table') {
                field.type = field.key;
                field.state = 'minmax';
                field.isActive = false;
            }
            break;
        case 'propName':
            field.icon = 'icon-plan-type';
            if (type === 'filter') {
                field.labelKey = 'propName';
            } else if (type === 'table') {
                field.type = 'propName';
                field.state = 'minmax';
                field.isActive = false;
            }

            break;
        case 'modelName':
            field.icon = 'icon-plan-type';
            if (type === 'filter') {
                field.labelKey = 'propType';
            } else if (type === 'table') {
                field.type = 'Model';
                field.state = 'minmax';
                field.isActive = false;
            }

            break;
        case 'propPricePerSqm':
        case 'salePrice':
        case 'rentPrice':
        case 'price2':
            let labelKey = 'budget';
            if (field.key === 'rentPrice' || field.key === 'salePrice')
                labelKey = 'rentPrice';
            if (field.key === 'price2')
                labelKey = 'price2Title';

            if (type === 'filter') {
                field.labelKey = labelKey;
                field.icon = 'icon-budget';
                field.type = 'range'
            } else if (type === 'table') {
                field.type = field.key;
                field.state = 'minmax';
                field.isActive = false;
                field.text = 'price';
            }
            break;
        case 'propType':
            field.icon = 'icon-bedroom';
            if (type === 'filter') {
                field.labelKey = field.key;
            } else if (type === 'table') {
                field.type = field.key;
                field.state = 'minmax';
                field.isActive = false;
            }
            break;
        case 'roomNum':
            field.icon = 'icon-bedroom';
            if (type === 'filter') {
                field.labelKey = 'rooms';
            } else if (type === 'table') {
                field.type = field.key;
                // field.type = 'rooms';
                field.state = 'minmax';
                field.isActive = false;
            }
            break;
        case 'totalSpace':
            field.icon = 'icon-area';
            if (type === 'filter') {
                field.labelKey = field.key;
                field.type = 'range';
            } else if (type === 'table') {
                field.type = field.key;
                field.state = 'minmax';
                field.isActive = false;
            }
            break;
        case 'toilets':
            field.icon = "icon-toilet";
            if (type === 'filter') {
                field.labelKey = 'Toilets';
            } else if (type === 'table') {
                field.type = 'Toilets';
                field.state = 'minmax';
                field.isActive = false;
            }
            break;
        case 'treatmentRoom':
            field.icon = 'icon-bed';
            if (type === 'filter') {
                field.labelKey = 'Treatment room';
            } else if (type === 'table') {
                field.type = 'Treatment room';
                field.state = 'minmax';
                field.isActive = false;
                field.visibilty = 'check-mark';
            }
            break;
        case 'tenantPrice':
            if (type === 'filter') {
                field.labelKey = field.key;
            } else if (type === 'table') {
                field.type = field.key;
                field.icon = "icon-gift";
                field.state = 'minmax';
                field.isActive = false;
                field.visibilty = 'check-mark';
            }
            break;
        default:
            field.icon = "icon-exclamation-mark";
            if (type === 'filter') {
                field.labelKey = field.key;
            } else if (type === 'table') {
                field.type = field.key;
                field.state = 'minmax';
                field.isActive = false;
            }
            break;
    }

    return field;
}

export function getRandomNumber(min, max, decimals = 0) {
    let randomNum = Math.random() * (max - min) + min;
    return decimals > 0 ? parseFloat(randomNum.toFixed(decimals)) : Math.floor(randomNum);
}

export function getTimestampForTime(hours, minutes) {
    const now = new Date();
    const fallbackDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        hours,
        minutes,
        0
    );

    return Math.floor(fallbackDate.getTime() / 1000);
}

export function getCallFunctionStack() {
    const err = new Error();
    console.log("Stack Trace:");
    console.log(err.stack);
    const stackLines = err.stack.split('\n');
    if (stackLines.length >= 3) { // Index 0 is 'Error', index 1 is current func, index 2 *might* be caller
        console.log("\nAttempting to find caller:");
        console.log(stackLines[2].trim());
    }
}

export function addFloorOrdinalSuffix(num) {
    const positive = Math.abs(num);
    const lastTwoDigits = positive % 100;
    const lastDigit = positive % 10;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
        return `<p>${num}<sup>th</sup> floor</p>`;
    }
    
    switch (lastDigit) {
        case 1: return `<p>${num}<sup>st</sup> floor</p>`;
        case 2: return `<p>${num}<sup>nd</sup> floor</p>`;
        case 3: return `<p>${num}<sup>rd</sup> floor</p>`;
        default: return `<p>${num}<sup>th</sup> floor</p>`;
    }
}
